import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Users, Monitor, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useServices } from "@/hooks/usePublicContent";

const ICONS = { "private-1-1": BookOpen, "small-group": Users, "virtual-1-1": Monitor };

export default function Services() {
  const services = useServices();

  return (
    <main className="bg-cream">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-testid="services-hero">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Services & Pricing</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink text-balance">
            Three ways to work with Jonna. All built on the same gold-standard method.
          </h1>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed">
            Each option uses Orton-Gillingham structured literacy and starts with a careful assessment.
            The right fit depends on your child's needs, your family's schedule, and your budget.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-16" data-testid="services-grid">
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = ICONS[s.slug] || BookOpen;
            const palette = [
              { card: "bg-white border-terracotta/20 ring-terracotta/10", chip: "bg-terracotta-100 text-terracotta-600", icon: "bg-terracotta text-white" },
              { card: "bg-white border-sage/20 ring-sage/10", chip: "bg-sage-100 text-sage-600", icon: "bg-sage text-white" },
              { card: "bg-white border-mustard/30 ring-mustard/10", chip: "bg-mustard-100 text-mustard-600", icon: "bg-mustard text-white" },
            ][i % 3];
            return (
              <Card key={s.id} className={`rounded-2xl ${palette.card} ring-1 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all`} data-testid={`service-card-${s.slug}`}>
                <CardContent className="p-7">
                  <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${palette.icon}`}><Icon size={20} /></div>
                  <h3 className="mt-5 font-display text-2xl font-semibold text-ink">{s.title}</h3>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className={`inline-flex rounded-full ${palette.chip} px-2.5 py-0.5 text-xs font-semibold`}>{s.price_label}</span>
                    <span className="text-xs text-ink/55">· {s.duration}</span>
                  </div>
                  <p className="mt-4 text-sm text-ink/70 leading-relaxed">{s.description}</p>
                  <ul className="mt-5 space-y-2.5">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-ink/80"><CheckCircle2 size={16} className="mt-0.5 flex-none text-sage-600" /> {f}</li>
                    ))}
                  </ul>
                  {s.recommended_for && (
                    <div className="mt-6 pt-5 border-t border-ink/5 text-xs text-ink/60">
                      <span className="font-medium text-ink/70">Best for: </span>{s.recommended_for}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="bg-white py-20" data-testid="services-faq-mini">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink text-balance">A few common questions</h2>
          <dl className="mt-10 space-y-7">
            <div>
              <dt className="font-medium text-ink">How long are sessions?</dt>
              <dd className="mt-1 text-ink/70 text-sm leading-relaxed">Each session is 60 minutes — the duration the research supports for skill-building intervention.</dd>
            </div>
            <div>
              <dt className="font-medium text-ink">Do you offer an initial assessment?</dt>
              <dd className="mt-1 text-ink/70 text-sm leading-relaxed">Yes. Before regular sessions begin, Jonna conducts a screening to identify exactly which skills need work. This shapes your child's custom plan.</dd>
            </div>
            <div>
              <dt className="font-medium text-ink">How do we get on the schedule?</dt>
              <dd className="mt-1 text-ink/70 text-sm leading-relaxed">Spots are limited — please join the waitlist and Jonna will reach out within a few business days to talk through your child's needs and recommend a plan.</dd>
            </div>
          </dl>
          <div className="mt-12">
            <Link to="/contact" data-testid="services-page-cta">
              <Button className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 px-7 shadow-md hover:-translate-y-0.5 transition-all">
                Join the Waitlist <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
